<template>
  <v-dialog
    v-model="dialog"
    max-width="1100"
    scrollable
    persistent
  >
    <v-card>
      <BarTitleDialog
        :title="isEdit ? 'Editar Tarifário' : 'Novo Tarifário'"
        @close="close()"
      />

      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="form.description"
              label="Descrição"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.date_start"
              label="Início de Vigência"
              outlined
              dense
              hide-details
              type="date"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.date_end"
              label="Fim de Vigência"
              outlined
              dense
              hide-details
              type="date"
            />
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <div class="subtitle-1 mb-1">
          Preços por Categoria de Quarto
        </div>
        <div class="caption grey--text mb-3">
          Tarifa FDS aplica-se em sábados e domingos. Tarifas especiais sobrescrevem os valores informados nas datas configuradas.
        </div>

        <v-expansion-panels
          accordion
          focusable
        >
          <v-expansion-panel
            v-for="item in form.items"
            :key="item.room_category_id"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center flex-wrap">
                <strong class="mr-3">{{ item.category_name }}</strong>
                <v-chip
                  small
                  outlined
                  color="primary"
                >
                  {{ item.pricing_mode === 'occupancy' ? `${item.occupancy_rates.length} variações` : 'Valor único' }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <OccupancyPricingEditor
                :value="item"
                :child-policies="resolvedChildPolicies"
                class="pt-4"
              />
              <v-row
                v-if="item.pricing_mode !== 'occupancy'"
                dense
                class="mt-2"
              >
                <v-col
                  cols="12"
                  sm="6"
                >
                  <vuetify-money
                    v-model="item.value"
                    label="Diária"
                    dense
                    outlined
                    hide-details="auto"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <vuetify-money
                    v-model="item.weekend_value"
                    label="FDS / feriado"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Herdar diária"
                  />
                </v-col>
              </v-row>
              <div
                v-if="isEdit"
                class="d-flex justify-end mt-3"
              >
                <v-btn
                  text
                  small
                  color="primary"
                  @click="openSpecialRates(item)"
                >
                  <v-icon
                    left
                    small
                  >
                    {{ icons.mdiCalendarMultiple }}
                  </v-icon>
                  Tarifas especiais
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div
          v-if="!isEdit"
          class="caption grey--text mt-2"
        >
          Salve o tarifário para configurar tarifas especiais por categoria.
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="close()"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          @click="save()"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>

    <RatePlanSpecialRatesModal
      v-model="specialRatesDialog"
      :rate-plan-id="form.id"
      :category="selectedCategory"
      :child-policies="resolvedChildPolicies"
    />
  </v-dialog>
</template>

<script>
import { mdiCalendarMultiple } from '@mdi/js'
import { mapActions } from 'vuex'
import BarTitleDialog from '@/components/BarTitleDialog.vue'
import OccupancyPricingEditor from '@/components/OccupancyPricingEditor.vue'
import RatePlanSpecialRatesModal from './RatePlanSpecialRatesModal.vue'

export default {
  components: { BarTitleDialog, OccupancyPricingEditor, RatePlanSpecialRatesModal },

  props: {
    value: { type: Boolean, default: false },
    editedItem: { type: Object, default: null },
    categories: { type: Array, default: () => [] },
    childPolicies: { type: Array, default: null },
  },

  data: () => ({
    loading: false,
    specialRatesDialog: false,
    selectedCategory: null,
    loadedChildPolicies: [],
    form: {
      description: '',
      date_start: null,
      date_end: null,
      items: [],
    },
    icons: { mdiCalendarMultiple },
  }),

  computed: {
    dialog: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
    isEdit() {
      return !!this.editedItem?.id
    },
    resolvedChildPolicies() {
      return this.childPolicies || this.loadedChildPolicies
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.initForm()
        this.loadChildPolicies()
      }
    },
  },

  methods: {
    ...mapActions('rate_plan', ['store', 'update']),

    initForm() {
      if (this.isEdit) {
        this.form = {
          id: this.editedItem.id,
          description: this.editedItem.description,
          date_start: this.editedItem.date_start,
          date_end: this.editedItem.date_end,
          items: this.categories.map(cat => {
            const existing = (this.editedItem.items || []).find(i => i.room_category_id === cat.id)

            return {
              room_category_id: cat.id,
              category_name: cat.description,
              value: existing ? existing.value : 0,
              weekend_value: existing ? existing.weekend_value : null,
              pricing_mode: existing?.pricing_mode || 'flat',
              occupancy_rates: JSON.parse(JSON.stringify(existing?.occupancy_rates || [])),
              child_rates: JSON.parse(JSON.stringify(existing?.child_rates || [])),
            }
          }),
        }
      } else {
        this.form = {
          description: '',
          date_start: null,
          date_end: null,
          items: this.categories.map(cat => ({
            room_category_id: cat.id,
            category_name: cat.description,
            value: 0,
            weekend_value: null,
            pricing_mode: 'flat',
            occupancy_rates: [],
            child_rates: [],
          })),
        }
      }
    },

    openSpecialRates(item) {
      this.selectedCategory = {
        id: item.room_category_id,
        description: item.category_name,
        pricing_mode: item.pricing_mode,
        occupancy_rates: item.occupancy_rates,
        child_rates: item.child_rates,
      }
      this.specialRatesDialog = true
    },

    async loadChildPolicies() {
      if (this.childPolicies) return
      try {
        const response = await this.$http.get('system/booking-engine-config')
        this.loadedChildPolicies = response.data?.data?.child_policies || []
      } catch (error) {
        this.loadedChildPolicies = []
      }
    },

    save() {
      const invalidOccupancyItem = this.form.items.find(item => item.pricing_mode === 'occupancy' && !item.occupancy_rates.length)
      if (invalidOccupancyItem) {
        this.$toast.error(`Adicione ao menos uma variação para ${invalidOccupancyItem.category_name}`)

        return
      }
      this.loading = true
      const action = this.isEdit ? this.update(this.form) : this.store(this.form)
      action
        .then(res => {
          this.$emit('saved', res.data)
          this.$toast.success('Tarifário salvo com sucesso!')
          this.close()
        })
        .catch(() => {})
        .finally(() => { this.loading = false })
    },

    close() {
      this.dialog = false
    },
  },
}
</script>
